'use client';

import React from 'react';
import CWHeaderSearch from '@/context/global/components/g00/g00-d00-g002-cw-header-search';
import CWLocaleToggleButton from '@/context/global/components/g00/g00-d00-g004-cw-local-toggle-button';
import CWHamburgerButton from '@/context/global/components/g00/g00-d00-g001-cw-hamburger-button';
import { useTranslations } from 'next-intl';
import CWSignInButton from '@/context/global/components/g01/g01-d01/g01-d01-singin-button';
import CWLocaleToggleButtonMobile from '@/context/global/components/g00/g00-d00-g008-cw-local-toggle-button-mobile';
import CWSignInButtonMobile from '@/context/global/components/g01/g01-d01/g01-d02-singin-button-mobile';
import { useRouter, usePathname } from 'next/navigation';

const getNavItems = (t: (key: string) => string) => [
  { label: t('HeaderNav.home'), href: '/', external: false },
  { label: t('HeaderNav.karaoke'), href: '/karaoke', external: false },
  { label: t('HeaderNav.playlist'), href: '/playlist', external: false },
  {
    label: t('HeaderNav.package'),
    href: 'https://www.ais.th/consumers/entertainment/music/karaoke/#package-platinum',
    external: true,
  },
];
function Logo() {
  return (
    <div className="relative flex shrink-0 flex-col content-stretch items-start justify-start gap-4 pr-1.5 md:gap-[49.549px]">
      <div className="relative h-10 w-[110px] shrink-0 md:h-[60px] md:w-[162.699px]">
        <div className="absolute left-0 top-0 h-10 w-[110px] md:h-[60px] md:w-[162.699px]">
          <button
            className="absolute left-0 top-0 h-10 w-[110px] cursor-pointer bg-cover bg-center bg-no-repeat md:h-[60px] md:w-[162.699px]"
            style={{ backgroundImage: `url('/assets/ais-karaoke-logo.png')` }}
            onClick={() => (window.location.href = '/')}
          />
        </div>
      </div>
    </div>
  );
}

function NavMenu() {
  const t = useTranslations();
  const pathname = usePathname();
  const navItems = getNavItems(t);

  // Determine selected index based on current pathname
  const selectedIndex = navItems.findIndex((item) => {
    if (item.external) return false;
    if (item.href === '/' && pathname === '/') return true;
    if (item.href !== '/' && pathname.startsWith(item.href)) return true;
    return false;
  });

  return (
    <nav
      className="xl1380:flex absolute left-1/2 box-border hidden translate-x-[-50%] translate-y-[-50%] content-stretch items-center justify-start gap-6 overflow-clip rounded-[12px] p-2.5"
      style={{ top: 'calc(50% - 0.5px)' }}
    >
      {navItems.map((item, i) => {
        const linkProps = item.external
          ? { href: item.href, target: '_blank', rel: 'noopener noreferrer' }
          : { href: item.href };

        const isActive = i === selectedIndex;

        return (
          <a
            key={i}
            {...linkProps}
            className={`shrink-0 text-nowrap px-2 py-0 text-[24px] not-italic leading-[32px] transition-colors duration-200 ${isActive ? 'border-b-2 border-ais-green font-bold text-ais-green' : 'font-medium text-white hover:text-ais-green'}`}
          >
            {item.label}
          </a>
        );
      })}
    </nav>
  );
}

function MobileMenu({ menuOpen }: { menuOpen: boolean; onSearchClick: () => void }) {
  const t = useTranslations();
  const pathname = usePathname();
  const navItems = getNavItems(t);

  // Determine selected index based on current pathname
  const selectedIndex = navItems.findIndex((item) => {
    if (item.external) return false;
    if (item.href === '/' && pathname === '/') return true;
    if (item.href !== '/' && pathname.startsWith(item.href)) return true;
    return false;
  });

  return (
    <nav
      className={`xl1380:hidden fixed left-0 top-[56px] z-50 flex w-full flex-col overflow-hidden bg-[#030606] bg-opacity-95 pl-8 transition-all duration-300 ease-in-out md:top-[80px] ${menuOpen ? 'pointer-events-auto max-h-screen py-6 opacity-100' : 'pointer-events-none max-h-0 py-0 opacity-0'}`}
      aria-hidden={!menuOpen}
    >
      <CWSignInButtonMobile />
      {navItems.map((item, i) => {
        const linkProps = item.external
          ? { href: item.href, target: '_blank', rel: 'noopener noreferrer' }
          : { href: item.href };

        const isActive = i === selectedIndex;

        return (
          <a
            key={i}
            {...linkProps}
            className={`py-3 text-2xl ${isActive ? 'font-bold text-ais-green' : 'font-medium text-white'}`}
          >
            {item.label}
          </a>
        );
      })}

      <CWLocaleToggleButtonMobile />
    </nav>
  );
}

export default function Header() {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const router = useRouter();
  const pathname = usePathname();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  React.useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const handleSearchClick = () => {
    router.push('/search');
    setMenuOpen(false); // Close mobile menu when navigating to search
  };

  return (
    <header className="fixed left-0 top-0 isolate z-[999] box-border flex min-h-[64px] w-full content-stretch items-center justify-between bg-transparent py-3 pl-2 pr-4 md:min-h-[80px] md:px-10 md:py-4 lg:px-20">
      {/* Gradient layer - visible when not scrolled */}
      <div
        className={`pointer-events-none absolute left-1/2 top-0 z-0 h-[120px] w-[1920px] -translate-x-1/2 bg-gradient-to-b from-black/70 via-black/30 to-transparent transition-opacity duration-300 ${scrolled ? 'opacity-0' : 'opacity-100'}`}
      />
      {/* Background layer - only visible when scrolled */}
      <div
        className={`pointer-events-none absolute left-0 top-0 z-0 h-full w-full transition-all duration-300 ${scrolled ? 'bg-background opacity-100 shadow-xl' : 'opacity-0'}`}
        style={
          scrolled
            ? {
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
              }
            : undefined
        }
      />

      <Logo />

      {/* Mobile: Search bar in center */}
      <div className="xl1380:hidden relative flex flex-1 justify-center px-3">
        <CWHeaderSearch className="w-full max-w-[200px]" />
      </div>

      {/* Mobile: Hamburger only */}
      <div className="xl1380:hidden pointer-events-auto relative z-[100]">
        <CWHamburgerButton onClick={() => setMenuOpen((v) => !v)} />
      </div>

      {/* Desktop: Search, Locale, SignIn */}
      <div className="xl1380:flex relative hidden content-stretch items-center justify-start gap-6">
        <CWHeaderSearch />
        <CWLocaleToggleButton />
        <CWSignInButton />
      </div>

      <NavMenu />
      <MobileMenu menuOpen={menuOpen} onSearchClick={handleSearchClick} />
    </header>
  );
}
